import type { ScreenplayEditorBlock } from './screenplayEditor';
import { createGeminiSpan } from './geminiDelivery';
import { createTedIntonationSpec } from './tedIntonation';
import { isPauseMarker } from './inlinePauses';
import type { DirectedEntry } from '../../supabase/functions/_shared/scene-directing';

/** Fill uncovered text; explicit pauses/unspoken passages are never overwritten. */
export function applyDirectedEntries(blocks: ScreenplayEditorBlock[], directions: DirectedEntry[], replace: boolean) {
  const byId = new Map(directions.map(d => [d.id, d.direction]));
  if (byId.size !== directions.length || directions.some(d => !blocks.some(b => b.id === d.id && ['action', 'dialogue'].includes(b.type)))) throw new Error('The directing result does not match this screenplay.');
  return blocks.map(block => {
    const direction = byId.get(block.id);
    if (!direction) return block;
    const existing = block.intonation?.spans ?? [];
    if (existing.some(s => s.start < 0 || s.end > block.text.length || s.end <= s.start || s.selectedText !== block.text.slice(s.start, s.end))) throw new Error('An existing direction no longer matches the text. Resolve it before assigning inflections.');
    const retained = existing.filter(s => !replace || isPauseMarker(s) || s.gemini?.unspoken || s.gemini?.pauseMs || s.pauseBeforeMs).sort((a, b) => a.start - b.start);
    let cursor = 0;
    const spans = [...retained];
    const append = (from: number, to: number) => {
      while (from < to && /\s/u.test(block.text[from])) from++;
      while (to > from && /\s/u.test(block.text[to - 1])) to--;
      if (to > from) spans.push(createGeminiSpan(from, to, block.text, direction));
    };
    for (const span of retained) { append(cursor, span.start); cursor = Math.max(cursor, span.end); }
    append(cursor, block.text.length);
    if (spans.length === retained.length && retained.length === existing.length) return block;
    return { ...block, intonation: { ...(block.intonation ?? createTedIntonationSpec()), sourceEmotion: 'neutral' as const,
      spans: spans.sort((a, b) => a.start - b.start) } };
  });
}
